<!--  TODO: what about create case?? check that -->

<script lang="ts">
	import { createEventDispatcher } from 'svelte';
	import { fade } from 'svelte/transition';
	import { logImageError } from '$lib/components/image/imageTransform';
	// getImageUrlFeatured,
	import { getResponsiveImageUrls } from '$lib/components/editor/imageHandler';

	export let image_url: string | undefined;
	export let alt = '...';
	export let boolCanEdit = false;
	export let view = false;
	// export let create = false;

	let loading = false;
	let hasError = false;
	const FALLBACK_IMAGE = '/img/959-9595822_lotus-flower-icon-emblem.png';

	// // Helper to determine if this is a new-style image path
	// const isNewImagePath = (path: string): boolean => {
	// 	// Check if it's a full URL first
	// 	if (path.startsWith('http')) {
	// 		const match = path.match(/\/storage\/v1\/object\/public\/images\/([^?]+)/);
	// 		return match ? match[1].startsWith('posts/') : false;
	// 	}
	// 	return path.startsWith('posts/');
	// };

	// Get the appropriate image URL based on the path type
	const getImageSource = (path: string | undefined) => {
		if (!path || path === FALLBACK_IMAGE) {
			return { src: FALLBACK_IMAGE, srcset: '' };
		}
		// If path is a full Supabase URL, extract the relative path
		if (path.startsWith('http')) {
			const match = path.match(/\/public\/images\/(.+?)(\?|$)/);
			if (match && match[1]) {
				path = match[1];
			}
		}
		// Now path is always relative
		const responsiveUrls = getResponsiveImageUrls(path);
		return {
			src: responsiveUrls[1].url, // e.g., 400px as default
			srcset: responsiveUrls.map((url) => `${url.url} ${url.width}w`).join(', ')
		};
	};

	const dispatch = createEventDispatcher();

	function onChange(evt: Event) {
		dispatch('change', evt);
	}

	function handleImageError(event: Event) {
		const img = event.target as HTMLImageElement;
		if (!hasError) {
			logImageError(img.src, 'Image failed to load');
			hasError = true;
			if (img.src !== FALLBACK_IMAGE) {
				img.src = FALLBACK_IMAGE;
			}
		}
	}

	$: imageSource = getImageSource(image_url);
</script>

{#if loading}
	<div class="h-60 rounded-2xl mx-auto object-contain bg-cosmicPurple800 animate-pulse" />
{:else if boolCanEdit}
	<!-- Editor view with upload capability -->
	<div class="mx-auto text-cosmicCyan max-w-full">
		<label class="block text-gray-500 text-sm my-1 hover:opacity-80 text-center" for="single">
			{#if image_url}
				<picture class="block">
					{#if !hasError && imageSource.srcset}
						<source
							type="image/avif"
							srcset={imageSource.srcset}
							sizes="(max-width: 400px) 400px,
								   (max-width: 800px) 800px,
								   1200px"
						/>
						<source
							type="image/webp"
							srcset={imageSource.srcset}
							sizes="(max-width: 400px) 400px,
								   (max-width: 800px) 800px,
								   1200px"
						/>
					{/if}
					<img
						class="rounded-lg max-h-60 w-auto mx-auto object-cover"
						src={imageSource.src}
						srcset={!hasError ? imageSource.srcset : ''}
						sizes="(max-width: 400px) 400px,
							   (max-width: 800px) 800px,
							   1200px"
						{alt}
						on:error={handleImageError}
					/>
				</picture>
			{:else}
				<div class="h-60 rounded-lg bg-cosmicPurple800 flex items-center justify-center">
					<span>Click to upload image</span>
				</div>
			{/if}
		</label>
		<input
			class="sr-only"
			type="file"
			id="single"
			accept="image/*"
			on:change={onChange}
			disabled={loading}
		/>
	</div>
{:else if view}
	<!-- Post view -->
	<div
		class="w-full max-w-2xl mx-auto rounded-xl flex justify-center items-center"
		style="min-height: 200px; max-height: 400px;"
	>
		<picture class="w-full">
			{#if !hasError && imageSource.srcset}
				<source
					type="image/avif"
					srcset={imageSource.srcset}
					sizes="(max-width: 400px) 400px,
						   (max-width: 800px) 800px,
						   1200px"
				/>
				<source
					type="image/webp"
					srcset={imageSource.srcset}
					sizes="(max-width: 400px) 400px,
						   (max-width: 800px) 800px,
						   1200px"
				/>
			{/if}
			<img
				class="rounded-xl object-contain max-h-96 max-w-full mx-auto"
				src={imageSource.src}
				srcset={!hasError ? imageSource.srcset : ''}
				sizes="(max-width: 400px) 400px,
					   (max-width: 800px) 800px,
					   1200px"
				{alt}
				on:error={handleImageError}
				in:fade={{ duration: 800 }}
			/>
		</picture>
	</div>
{:else}
	<!-- Thumbnail view for media feed -->
	<div class="flex justify-center items-center h-36 w-full">
		<picture class="w-full">
			{#if !hasError && imageSource.src}
				{#if imageSource.srcset}
					<source
						type="image/avif"
						srcset={imageSource.srcset}
						sizes="(max-width: 300px) 300px,
							   (max-width: 400px) 400px,
							   600px"
					/>
					<source
						type="image/webp"
						srcset={imageSource.srcset}
						sizes="(max-width: 300px) 300px,
							   (max-width: 400px) 400px,
							   600px"
					/>
				{/if}
				<img
					loading="lazy"
					decoding="async"
					class="flex h-24 sm:h-36 w-full rounded-lg md:rounded-xl object-cover bg-cosmicPurplebg aspect-none"
					src={imageSource.src}
					srcset={!hasError ? imageSource.srcset : ''}
					sizes="(max-width: 300px) 300px,
						   (max-width: 400px) 400px,
						   600px"
					width="100%"
					height="auto"
					{alt}
					on:error={handleImageError}
				/>
			{:else}
				<img
					loading="lazy"
					decoding="async"
					class="flex h-24 sm:h-36 w-full rounded-lg md:rounded-xl object-cover bg-cosmicPurplebg aspect-none"
					src={FALLBACK_IMAGE}
					width="100%"
					height="auto"
					{alt}
				/>
			{/if}
		</picture>
	</div>
{/if}
